/**
 * "How we run it" (the playbook, as numbered steps) and "How we track it"
 * (the matching rules in plain words, editable as chips, plus the source's
 * other monthly cost).
 */
import { useEffect, useMemo, useState } from "react";
import { BookOpen, ChevronDown, Pencil, Radar, X } from "lucide-react";
import { Button, Input, Label, Textarea, useToast } from "@/components/ui";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Empty, Section, num, usd } from "@/features/command/ui";
import { AFFILIATE_CHANNEL, clickIdLabel, errMsg } from "../shared";
import type { MatchRules, SourceRow } from "../types";

// ─── Playbook ────────────────────────────────────────────────────────────────

export function steps(text: string | null | undefined): string[] {
  return String(text ?? "")
    .split(/\n+/)
    .map((l) => l.trim().replace(/^(\d+[.)]|[-*•])\s*/, ""))
    .filter(Boolean);
}

export function PlaybookSection({ source, onPatch }: { source: SourceRow; onPatch: (b: Partial<SourceRow>) => Promise<boolean> }) {
  const [editing, setEditing] = useState(false);
  const [playbook, setPlaybook] = useState(source.playbook ?? "");
  const [description, setDescription] = useState(source.description ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!editing) { setPlaybook(source.playbook ?? ""); setDescription(source.description ?? ""); }
  }, [source.playbook, source.description, editing]);
  const list = steps(source.playbook);

  const save = async () => {
    setSaving(true);
    const ok = await onPatch({ playbook: playbook.trim() || null, description: description.trim() || null });
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <Section
      title="How we run it"
      icon={BookOpen}
      action={!editing && (
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)} leftIcon={<Pencil className="h-3.5 w-3.5" />}>Edit</Button>
      )}
    >
      {editing ? (
        <div className="space-y-3">
          <div>
            <Label htmlFor="src-desc">What it is</Label>
            <Textarea id="src-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="min-h-0" maxLength={2000} placeholder="One or two lines on what this source is." />
          </div>
          <div>
            <Label htmlFor="src-playbook">Steps (one per line)</Label>
            <Textarea id="src-playbook" value={playbook} onChange={(e) => setPlaybook(e.target.value)} rows={8} maxLength={20000} placeholder={"Research what's hot this week\nRecord on Monday\nSchedule one post a day"} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={() => void save()} loading={saving}>Save</Button>
          </div>
        </div>
      ) : list.length === 0 ? (
        <Empty icon={BookOpen} title="No playbook yet" action={<Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Write the steps</Button>}>
          Write how this source is run, step by step, so anyone on the team (or an AI employee) can pick it up.
        </Empty>
      ) : (
        <ol className="space-y-2.5">
          {list.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-[11px] font-semibold text-indigo-300 ring-1 ring-inset ring-indigo-400/25">{i + 1}</span>
              <span className="text-sm leading-relaxed text-zinc-200">{s}</span>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

// ─── Tracking rules ──────────────────────────────────────────────────────────

const AFF_WORD: Record<string, string> = { any: "any affiliate", partner: "a JV partner", pro: "a pro affiliate", creator: "a content creator", casual: "a casual sharer" };

interface Rule {
  key: string;
  title: string;
  hint: string;
  say: (w: string[]) => string;
  placeholder: string;
}

const list = (w: string[], max = 8) => (w.length > max ? `${w.slice(0, max).join(", ")} and ${w.length - max} more` : w.join(", "));

const RULES: Rule[] = [
  { key: "click_ids", title: "Ad clicks", hint: "Ad platforms add a click code to every ad click (gclid, ttclid, msclkid).", placeholder: "gclid",
    say: (w) => `Ad clicks from ${list([...new Set(w.map((x) => `${clickIdLabel(x)} (${x})`))])}` },
  { key: "utm_campaign", title: "Campaign name contains", hint: "Words in a link's campaign tag (utm_campaign).", placeholder: "retarget",
    say: (w) => `Links whose campaign name contains ${list(w)}` },
  { key: "utm_source", title: "Link source is", hint: "A link's source tag (utm_source), e.g. instagram.", placeholder: "instagram",
    say: (w) => `Links tagged source = ${list(w)}` },
  { key: "utm_medium", title: "Link medium is", hint: "A link's medium tag (utm_medium), e.g. social or paid.", placeholder: "social",
    say: (w) => `Links tagged medium = ${list(w)}` },
  { key: "referrer", title: "Came from the website", hint: "The site they clicked through from.", placeholder: "instagram.com",
    say: (w) => `Visitors coming from ${list(w)}` },
  { key: "landing_page", title: "First page starts with", hint: "The page on our site they landed on first.", placeholder: "/partners",
    say: (w) => `Visitors who first land on ${list(w)}` },
  { key: "affiliate", title: "Referral code from", hint: "partner, pro, creator, casual, or any.", placeholder: "partner",
    say: (w) => `People who signed up with a referral code from ${w.map((x) => AFF_WORD[x] ?? x).join(" or ")}` },
  { key: "affiliate_channel", title: "Affiliate channel", hint: "syncsuite (our program), clickbank or other.", placeholder: "syncsuite",
    say: (w) => `Only affiliates on ${w.map((x) => AFFILIATE_CHANNEL[x] ?? x).join(" or ")}` },
  { key: "sources", title: "Platform signup tag", hint: "How the platform tagged the signup, e.g. agency_handoff.", placeholder: "agency_handoff",
    say: (w) => `Signups the platform tagged ${list(w)}` },
];

const clean = (m: MatchRules): MatchRules => {
  const out: MatchRules = {};
  for (const [k, v] of Object.entries(m)) {
    const words = [...new Set((v ?? []).map((x) => String(x).trim().toLowerCase()).filter(Boolean))];
    if (words.length) out[k] = words;
  }
  return out;
};

export function TrackingSection({ source, onPatch, onRecomputed }: {
  source: SourceRow;
  onPatch: (b: Partial<SourceRow>) => Promise<boolean>;
  onRecomputed: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MatchRules>({});
  const [saving, setSaving] = useState(false);
  const [cost, setCost] = useState(source.monthly_cost_usd == null ? "" : String(Number(source.monthly_cost_usd)));
  const [savingCost, setSavingCost] = useState(false);
  const match = useMemo(() => clean(source.match ?? {}), [source.match]);
  const known = new Set(RULES.map((r) => r.key));
  const extra = Object.keys(match).filter((k) => !known.has(k));

  useEffect(() => { setCost(source.monthly_cost_usd == null ? "" : String(Number(source.monthly_cost_usd))); }, [source.monthly_cost_usd]);

  const startEdit = () => { setDraft({ ...match }); setEditing(true); setOpen(true); };

  const save = async () => {
    setSaving(true);
    const ok = await onPatch({ match: clean(draft) });
    setSaving(false);
    if (!ok) return;
    setEditing(false);
    toast.info("Re-crediting everyone…", "People move to the right source in a moment.");
    try {
      const r = await api.post<{ contacts: number }>("/sources/recompute");
      toast.success("Everyone re-credited", `${num(r.contacts)} people checked against the new rules.`);
      onRecomputed();
    } catch (e) {
      toast.error("Couldn't re-credit people", errMsg(e));
    }
  };

  const saveCost = async () => {
    const v = cost.trim() === "" ? null : Number(cost);
    if (v != null && (!Number.isFinite(v) || v < 0)) { toast.error("Enter a dollar amount"); return; }
    setSavingCost(true);
    const ok = await onPatch({ monthly_cost_usd: v });
    setSavingCost(false);
    if (ok) { toast.success(v ? `Counting ${usd(v)} a month` : "Monthly cost cleared"); onRecomputed(); }
  };
  const costChanged = (cost.trim() === "" ? null : Number(cost)) !== (source.monthly_cost_usd == null ? null : Number(source.monthly_cost_usd));

  const sentences = RULES.filter((r) => match[r.key]?.length).map((r) => ({ key: r.key, text: r.say(match[r.key]!) }));
  for (const k of extra) sentences.push({ key: k, text: `${k}: ${list(match[k]!)}` });

  return (
    <section className="cc-card min-w-0">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
        <Radar className="h-4 w-4 shrink-0 text-indigo-300" />
        <h2 className="text-sm font-semibold text-zinc-100">How we track it</h2>
        <span className="rounded-full bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Advanced</span>
        <ChevronDown className={cn("ml-auto h-4 w-4 text-zinc-500 transition-transform", !open && "-rotate-90")} />
      </button>
      {open && (
        <div className="space-y-4 border-t border-zinc-800/70 p-4">
          {!editing ? (
            <>
              <div>
                <p className="mb-2 text-xs text-zinc-500">Someone counts here when they arrive through any of these:</p>
                {sentences.length ? (
                  <ul className="space-y-1.5">
                    {sentences.map((s) => (
                      <li key={s.key} className="flex gap-2 text-sm text-zinc-200">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-indigo-400" />
                        <span>{s.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : source.kind === "direct" ? (
                  <p className="text-sm text-zinc-300">Everyone with no source on any visit lands here. Nothing to set up. To shrink it, tag every link you share.</p>
                ) : (
                  <p className="text-sm text-zinc-300">No rules yet, so nobody is credited here. Add the words its links carry.</p>
                )}
              </div>
              <p className="text-xs leading-relaxed text-zinc-500">
                A referral code always wins. Otherwise the last place someone came from before signing up gets the credit,
                and the most specific clue decides: an ad click beats a campaign name, which beats a link's source, then its medium, then the website.
              </p>
              <Button variant="secondary" size="sm" onClick={startEdit} leftIcon={<Pencil className="h-3.5 w-3.5" />}>Edit the rules</Button>
            </>
          ) : (
            <div className="space-y-3">
              {[...RULES, ...extra.map((k) => ({ key: k, title: k, hint: "", placeholder: "", say: () => "" }))].map((r) => (
                <ChipRow
                  key={r.key}
                  title={r.title}
                  hint={r.hint}
                  placeholder={r.placeholder}
                  words={draft[r.key] ?? []}
                  onChange={(w) => setDraft((d) => ({ ...d, [r.key]: w }))}
                />
              ))}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" onClick={() => void save()} loading={saving}>Save and re-credit</Button>
              </div>
            </div>
          )}

          <div className="border-t border-zinc-800/70 pt-4">
            <Label htmlFor="src-cost">Other monthly cost (tools, contractors)</Label>
            <div className="flex items-center gap-2">
              <div className="w-40">
                <Input
                  id="src-cost" type="number" min={0} step="1" inputMode="decimal" value={cost}
                  onChange={(e) => setCost(e.target.value)} placeholder="0" leftIcon={<span className="text-xs">$</span>}
                  onKeyDown={(e) => { if (e.key === "Enter" && costChanged) void saveCost(); }}
                />
              </div>
              <span className="text-xs text-zinc-500">a month</span>
              {costChanged && <Button size="sm" onClick={() => void saveCost()} loading={savingCost}>Save</Button>}
            </div>
            <p className="mt-1.5 text-xs text-zinc-500">Counted as spend, spread across each period, so cost per customer stays honest.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function ChipRow({ title, hint, placeholder, words, onChange }: {
  title: string; hint: string; placeholder: string; words: string[]; onChange: (w: string[]) => void;
}) {
  const [v, setV] = useState("");
  const add = () => {
    const parts = v.split(/[,\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    onChange([...new Set([...words, ...parts])]);
    setV("");
  };
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-3">
      <div className="text-xs font-medium text-zinc-200">{title}</div>
      {hint && <div className="mt-0.5 text-[11px] leading-snug text-zinc-500">{hint}</div>}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {words.map((w) => (
          <span key={w} className="inline-flex items-center gap-1 rounded-full bg-indigo-500/12 py-0.5 pl-2 pr-1 text-xs text-indigo-200 ring-1 ring-inset ring-indigo-500/25">
            {w}
            <button type="button" onClick={() => onChange(words.filter((x) => x !== w))} className="rounded-full p-0.5 text-indigo-300/70 hover:bg-indigo-500/20 hover:text-indigo-100" aria-label={`Remove ${w}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
            if (e.key === "Backspace" && !v && words.length) onChange(words.slice(0, -1));
          }}
          onBlur={add}
          placeholder={words.length ? "Add…" : `e.g. ${placeholder || "word"}`}
          className="h-6 min-w-[7rem] flex-1 bg-transparent text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
        />
      </div>
    </div>
  );
}

